import React, { useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled, { keyframes, css } from 'styled-components';
import { getMCQuestions, getFRQuestions, MCQuestion, FRQuestion } from '../data/questions';
import { saveQuizScore } from '../data/progress';
import { getConcept, getUnit } from '../data/units';

type QuizMode = 'mc' | 'fr';
type MCState = 'answering' | 'reviewed' | 'complete';
type SelfAssessment = 'all' | 'most' | 'practice' | null;

interface FRPartReveal {
  [partIndex: number]: boolean;
}

function shuffleArray<T>(arr: T[]): T[] {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

const OPTION_LETTERS = ['A', 'B', 'C', 'D'];

const QuizScreen: React.FC = () => {
  const { unitId, conceptId } = useParams<{ unitId: string; conceptId: string }>();
  const navigate = useNavigate();

  const unit = unitId ? getUnit(unitId) : undefined;
  const concept = unitId && conceptId ? getConcept(unitId, conceptId) : undefined;

  const rawMCQuestions = useMemo(
    () => (unitId && conceptId ? getMCQuestions(unitId, conceptId) : []),
    [unitId, conceptId],
  );
  const frQuestions = useMemo(
    () => (unitId && conceptId ? getFRQuestions(unitId, conceptId) : []),
    [unitId, conceptId],
  );

  const hasContent = rawMCQuestions.length > 0 || frQuestions.length > 0;

  // ── Mode tab ──────────────────────────────────────────────────────────────
  const [mode, setMode] = useState<QuizMode>(rawMCQuestions.length > 0 ? 'mc' : 'fr');

  // ── MC state ──────────────────────────────────────────────────────────────
  const [mcQuestions, setMcQuestions] = useState<MCQuestion[]>(() => shuffleArray(rawMCQuestions));
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [mcState, setMcState] = useState<MCState>('answering');
  const [correctCount, setCorrectCount] = useState(0);
  const [scoreSaved, setScoreSaved] = useState(false);

  // ── FR state ──────────────────────────────────────────────────────────────
  const [currentFRIndex, setCurrentFRIndex] = useState(0);
  const [revealedParts, setRevealedParts] = useState<FRPartReveal>({});
  const [selfAssessment, setSelfAssessment] = useState<SelfAssessment>(null);
  const [frComplete, setFrComplete] = useState(false);

  const currentMC: MCQuestion | undefined = mcQuestions[currentIndex];
  const currentFR: FRQuestion | undefined = frQuestions[currentFRIndex];
  const totalMC = mcQuestions.length;
  const totalFR = frQuestions.length;

  // ── MC handlers ───────────────────────────────────────────────────────────
  const handleSelectOption = useCallback(
    (optionIndex: number) => {
      if (mcState !== 'answering' || !currentMC) return;
      setSelectedOption(optionIndex);
      setMcState('reviewed');
      if (optionIndex === currentMC.correctIndex) {
        setCorrectCount((prev) => prev + 1);
      }
    },
    [mcState, currentMC],
  );

  const handleNextQuestion = useCallback(() => {
    if (currentIndex + 1 >= totalMC) {
      setMcState('complete');
      // Save score
      if (!scoreSaved && unitId && conceptId) {
        const finalCorrect =
          selectedOption === currentMC?.correctIndex ? correctCount : correctCount;
        saveQuizScore(unitId, conceptId, finalCorrect, totalMC);
        setScoreSaved(true);
      }
    } else {
      setCurrentIndex((prev) => prev + 1);
      setSelectedOption(null);
      setMcState('answering');
    }
  }, [currentIndex, totalMC, scoreSaved, unitId, conceptId, correctCount, selectedOption, currentMC]);

  const handleTryAgain = useCallback(() => {
    setMcQuestions(shuffleArray(rawMCQuestions));
    setCurrentIndex(0);
    setSelectedOption(null);
    setMcState('answering');
    setCorrectCount(0);
    setScoreSaved(false);
    setRevealedParts({});
    setSelfAssessment(null);
    setFrComplete(false);
    setCurrentFRIndex(0);
  }, [rawMCQuestions]);

  // ── FR handlers ───────────────────────────────────────────────────────────
  const handleRevealPart = useCallback(
    (partIndex: number) => {
      if (!currentFR) return;
      // Enforce sequential reveal
      if (partIndex > 0 && !revealedParts[partIndex - 1]) return;
      setRevealedParts((prev) => ({ ...prev, [partIndex]: true }));
      // Check if all parts are now revealed
      if (partIndex === currentFR.parts.length - 1) {
        // All parts revealed for this question
      }
    },
    [currentFR, revealedParts],
  );

  const allFRPartsRevealed = currentFR
    ? currentFR.parts.every((_, i) => revealedParts[i])
    : false;

  const handleSelfAssess = useCallback(
    (assessment: SelfAssessment) => {
      setSelfAssessment(assessment);
      setFrComplete(true);
      // Save a rough score based on self-assessment
      if (unitId && conceptId && currentFR) {
        const total = currentFR.parts.reduce((sum, p) => sum + p.points, 0);
        let score = 0;
        if (assessment === 'all') score = total;
        else if (assessment === 'most') score = Math.round(total * 0.7);
        else score = Math.round(total * 0.3);
        saveQuizScore(unitId, conceptId, score, total);
      }
    },
    [unitId, conceptId, currentFR],
  );

  // ── Score helpers ─────────────────────────────────────────────────────────
  const scorePct = totalMC > 0 ? Math.round((correctCount / totalMC) * 100) : 0;

  const getEncouragingMessage = (pct: number): string => {
    if (pct === 100) return 'Perfect score! You have mastered this concept!';
    if (pct >= 80) return 'Great job! You have a strong understanding of this material.';
    if (pct >= 60) return 'Good effort! Review the explanations to strengthen your knowledge.';
    if (pct >= 40) return 'Keep practicing! Focus on the questions you missed.';
    return 'This is a tough topic. Review the lesson and try again — you will get it!';
  };

  // ── No content fallback ───────────────────────────────────────────────────
  if (!unit || !concept) {
    return (
      <Container>
        <EmptyState>
          <EmptyIcon>?</EmptyIcon>
          <EmptyTitle>Concept not found</EmptyTitle>
          <BackLink onClick={() => navigate(unitId ? `/unit/${unitId}` : '/')}>
            Go back
          </BackLink>
        </EmptyState>
      </Container>
    );
  }

  if (!hasContent) {
    return (
      <Container>
        <Header $color={unit.color}>
          <HeaderInner>
            <BackButton onClick={() => navigate(`/unit/${unitId}`)}>
              <BackArrow>&larr;</BackArrow>
              <span>Back to Unit</span>
            </BackButton>
            <HeaderTitle>{concept.title}</HeaderTitle>
            <HeaderSub>Unit {unit.number}: {unit.title}</HeaderSub>
          </HeaderInner>
        </Header>
        <EmptyState>
          <EmptyIcon>&#9997;</EmptyIcon>
          <EmptyTitle>Questions coming soon!</EmptyTitle>
          <EmptyDescription>
            We are working on practice questions for this concept. Check back later!
          </EmptyDescription>
          <BackLink onClick={() => navigate(`/unit/${unitId}`)}>
            Back to {unit.title}
          </BackLink>
        </EmptyState>
      </Container>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Container>
      <Header $color={unit.color}>
        <HeaderInner>
          <BackButton onClick={() => navigate(`/unit/${unitId}`)}>
            <BackArrow>&larr;</BackArrow>
            <span>Back to Unit</span>
          </BackButton>
          <HeaderTitle>{concept.title}</HeaderTitle>
          <HeaderSub>Unit {unit.number}: {unit.title} &mdash; Quiz</HeaderSub>
        </HeaderInner>
      </Header>

      <Main>
        {/* Tab Switcher */}
        {rawMCQuestions.length > 0 && frQuestions.length > 0 && (
          <TabBar>
            <Tab
              $active={mode === 'mc'}
              $color={unit.color}
              onClick={() => setMode('mc')}
            >
              Multiple Choice
            </Tab>
            <Tab
              $active={mode === 'fr'}
              $color={unit.color}
              onClick={() => setMode('fr')}
            >
              Free Response
            </Tab>
          </TabBar>
        )}

        {/* ── Multiple Choice Mode ──────────────────────────────────────── */}
        {mode === 'mc' && mcQuestions.length > 0 && (
          <>
            {mcState !== 'complete' && currentMC && (
              <QuizCard>
                <QuestionCounter>
                  Question {currentIndex + 1} of {totalMC}
                </QuestionCounter>
                <ProgressBarTrack>
                  <ProgressBarFill
                    $pct={((currentIndex + 1) / totalMC) * 100}
                    $color={unit.color}
                  />
                </ProgressBarTrack>

                <QuestionText>{currentMC.question}</QuestionText>

                <OptionsGrid>
                  {currentMC.options.map((option, i) => {
                    const isSelected = selectedOption === i;
                    const isCorrect = i === currentMC.correctIndex;
                    const showResult = mcState === 'reviewed';

                    let variant: 'default' | 'correct' | 'incorrect' | 'missed' = 'default';
                    if (showResult) {
                      if (isSelected && isCorrect) variant = 'correct';
                      else if (isSelected && !isCorrect) variant = 'incorrect';
                      else if (!isSelected && isCorrect) variant = 'missed';
                    }

                    return (
                      <OptionCard
                        key={i}
                        $variant={variant}
                        $disabled={mcState !== 'answering'}
                        onClick={() => handleSelectOption(i)}
                      >
                        <OptionLetter
                          $variant={variant}
                          $color={unit.color}
                          $selected={isSelected && mcState === 'answering'}
                        >
                          {OPTION_LETTERS[i]}
                        </OptionLetter>
                        <OptionText>{option}</OptionText>
                        {showResult && isCorrect && (
                          <OptionIcon $type="correct">&#10003;</OptionIcon>
                        )}
                        {showResult && isSelected && !isCorrect && (
                          <OptionIcon $type="incorrect">&#10007;</OptionIcon>
                        )}
                      </OptionCard>
                    );
                  })}
                </OptionsGrid>

                {mcState === 'reviewed' && (
                  <FeedbackArea>
                    {selectedOption === currentMC.correctIndex ? (
                      <FeedbackBanner $type="correct">
                        <FeedbackIcon>&#10003;</FeedbackIcon>
                        Correct!
                      </FeedbackBanner>
                    ) : (
                      <FeedbackBanner $type="incorrect">
                        <FeedbackIcon>&#10007;</FeedbackIcon>
                        Incorrect &mdash; the answer is {OPTION_LETTERS[currentMC.correctIndex]}
                      </FeedbackBanner>
                    )}

                    <ExplanationBox>
                      <ExplanationLabel>Explanation</ExplanationLabel>
                      <ExplanationText>{currentMC.explanation}</ExplanationText>
                    </ExplanationBox>

                    <NextButton $color={unit.color} onClick={handleNextQuestion}>
                      {currentIndex + 1 >= totalMC ? 'See Results' : 'Next Question'} &rarr;
                    </NextButton>
                  </FeedbackArea>
                )}
              </QuizCard>
            )}

            {mcState === 'complete' && (
              <ResultsCard>
                <ResultsHeader>Quiz Complete!</ResultsHeader>

                <ScoreCircleWrapper>
                  <ScoreCircleSVG viewBox="0 0 120 120">
                    <ScoreCircleBg cx="60" cy="60" r="52" />
                    <ScoreCircleFill
                      cx="60"
                      cy="60"
                      r="52"
                      $pct={scorePct}
                      $color={scorePct >= 60 ? '#4caf50' : '#f44336'}
                    />
                  </ScoreCircleSVG>
                  <ScoreCircleText>
                    <ScoreNumber>{scorePct}%</ScoreNumber>
                    <ScoreDetail>
                      {correctCount} of {totalMC} correct
                    </ScoreDetail>
                  </ScoreCircleText>
                </ScoreCircleWrapper>

                <EncouragingMessage>{getEncouragingMessage(scorePct)}</EncouragingMessage>

                <ResultsActions>
                  <ActionButton $variant="outline" $color={unit.color} onClick={handleTryAgain}>
                    Try Again
                  </ActionButton>
                  <ActionButton
                    $variant="filled"
                    $color={unit.color}
                    onClick={() =>
                      navigate(`/unit/${unitId}/concept/${conceptId}/simulation`)
                    }
                  >
                    Next: Simulation &rarr;
                  </ActionButton>
                  <ActionButton
                    $variant="subtle"
                    $color="#666"
                    onClick={() => navigate(`/unit/${unitId}`)}
                  >
                    Back to Unit
                  </ActionButton>
                </ResultsActions>
              </ResultsCard>
            )}
          </>
        )}

        {/* ── Free Response Mode ────────────────────────────────────────── */}
        {mode === 'fr' && frQuestions.length > 0 && currentFR && !frComplete && (
          <QuizCard>
            {totalFR > 1 && (
              <QuestionCounter>
                Question {currentFRIndex + 1} of {totalFR}
              </QuestionCounter>
            )}

            <FRQuestionStem>{currentFR.question}</FRQuestionStem>

            <FRPartsWrapper>
              {currentFR.parts.map((part, i) => {
                const isRevealed = !!revealedParts[i];
                const canReveal = i === 0 || !!revealedParts[i - 1];

                return (
                  <FRPartCard key={i} $revealed={isRevealed}>
                    <FRPartHeader>
                      <FRPartLabel>({String.fromCharCode(97 + i)})</FRPartLabel>
                      <FRPartQuestion>{part.question}</FRPartQuestion>
                      <FRPartPoints>{part.points} pt{part.points !== 1 ? 's' : ''}</FRPartPoints>
                    </FRPartHeader>

                    {!isRevealed && (
                      <RevealButton
                        $color={unit.color}
                        $enabled={canReveal}
                        onClick={() => canReveal && handleRevealPart(i)}
                        disabled={!canReveal}
                      >
                        {canReveal
                          ? 'Reveal Solution'
                          : `Reveal part (${String.fromCharCode(97 + i - 1)}) first`}
                      </RevealButton>
                    )}

                    {isRevealed && (
                      <FRSolutionWrapper>
                        <FRSolutionLabel>Solution</FRSolutionLabel>
                        <FRSolutionText>{part.solution}</FRSolutionText>
                      </FRSolutionWrapper>
                    )}
                  </FRPartCard>
                );
              })}
            </FRPartsWrapper>

            {allFRPartsRevealed && (
              <SelfAssessArea>
                <SelfAssessTitle>How did you do?</SelfAssessTitle>
                <SelfAssessOptions>
                  <SelfAssessButton
                    $variant="good"
                    onClick={() => handleSelfAssess('all')}
                  >
                    <SelfAssessEmoji>&#9733;</SelfAssessEmoji>
                    Got it all
                  </SelfAssessButton>
                  <SelfAssessButton
                    $variant="ok"
                    onClick={() => handleSelfAssess('most')}
                  >
                    <SelfAssessEmoji>&#9734;</SelfAssessEmoji>
                    Got most of it
                  </SelfAssessButton>
                  <SelfAssessButton
                    $variant="needs"
                    onClick={() => handleSelfAssess('practice')}
                  >
                    <SelfAssessEmoji>&#8634;</SelfAssessEmoji>
                    Need more practice
                  </SelfAssessButton>
                </SelfAssessOptions>
              </SelfAssessArea>
            )}
          </QuizCard>
        )}

        {mode === 'fr' && frComplete && (
          <ResultsCard>
            <ResultsHeader>Free Response Complete!</ResultsHeader>

            <SelfAssessResult>
              {selfAssessment === 'all' && (
                <SelfAssessResultText $variant="good">
                  Excellent! You nailed this free response question.
                </SelfAssessResultText>
              )}
              {selfAssessment === 'most' && (
                <SelfAssessResultText $variant="ok">
                  Good work! You understood most of the solution. Review the parts you missed.
                </SelfAssessResultText>
              )}
              {selfAssessment === 'practice' && (
                <SelfAssessResultText $variant="needs">
                  No worries! Go back to the lesson and review, then give it another try.
                </SelfAssessResultText>
              )}
            </SelfAssessResult>

            <ResultsActions>
              <ActionButton $variant="outline" $color={unit.color} onClick={handleTryAgain}>
                Try Again
              </ActionButton>
              <ActionButton
                $variant="filled"
                $color={unit.color}
                onClick={() =>
                  navigate(`/unit/${unitId}/concept/${conceptId}/simulation`)
                }
              >
                Next: Simulation &rarr;
              </ActionButton>
              <ActionButton
                $variant="subtle"
                $color="#666"
                onClick={() => navigate(`/unit/${unitId}`)}
              >
                Back to Unit
              </ActionButton>
            </ResultsActions>
          </ResultsCard>
        )}
      </Main>
    </Container>
  );
};

export default QuizScreen;

/* ─── Animations ──────────────────────────────────────────────────────────────── */

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const slideReveal = keyframes`
  from { opacity: 0; max-height: 0; padding-top: 0; padding-bottom: 0; }
  to   { opacity: 1; max-height: 600px; padding-top: 16px; padding-bottom: 16px; }
`;

const circleAnim = keyframes`
  from { stroke-dashoffset: 327; }
`;

/* ─── Styled Components ───────────────────────────────────────────────────────── */

const Container = styled.div`
  min-height: 100vh;
  background: #f5f7fa;
`;

const Header = styled.header<{ $color: string }>`
  background: linear-gradient(
    135deg,
    ${(p) => p.$color} 0%,
    ${(p) => p.$color}dd 60%,
    ${(p) => p.$color}bb 100%
  );
  padding: 32px 24px 40px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -40%;
    right: -10%;
    width: 360px;
    height: 360px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.07);
    pointer-events: none;
  }
`;

const HeaderInner = styled.div`
  max-width: 800px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 8px;
  padding: 6px 14px;
  color: #ffffff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;
  margin-bottom: 16px;

  &:hover {
    background: rgba(255, 255, 255, 0.25);
  }
`;

const BackArrow = styled.span`
  font-size: 16px;
  line-height: 1;
`;

const HeaderTitle = styled.h1`
  font-size: 28px;
  font-weight: 800;
  color: #ffffff;
  margin: 0 0 4px;
  letter-spacing: -0.3px;
  line-height: 1.15;

  @media (max-width: 600px) {
    font-size: 22px;
  }
`;

const HeaderSub = styled.p`
  font-size: 14px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.85);
  margin: 0;
`;

const Main = styled.main`
  max-width: 800px;
  margin: -20px auto 0;
  padding: 0 24px 60px;
  position: relative;
  z-index: 2;
`;

/* ── Tab Bar ──────────────────────────────────────────────────────────────────── */

const TabBar = styled.div`
  display: flex;
  background: #ffffff;
  border-radius: 12px;
  padding: 4px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  margin-bottom: 20px;
  gap: 4px;
`;

const Tab = styled.button<{ $active: boolean; $color: string }>`
  flex: 1;
  padding: 10px 16px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  ${(p) =>
    p.$active
      ? css`
          background: ${p.$color};
          color: #ffffff;
          box-shadow: 0 2px 8px ${p.$color}44;
        `
      : css`
          background: transparent;
          color: #666;
          &:hover {
            background: #f0f1f5;
          }
        `}
`;

/* ── Quiz Card Shell ──────────────────────────────────────────────────────────── */

const QuizCard = styled.div`
  background: #ffffff;
  border-radius: 16px;
  padding: 28px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06), 0 4px 16px rgba(0, 0, 0, 0.04);
  animation: ${fadeIn} 0.3s ease;

  @media (max-width: 600px) {
    padding: 20px 16px;
  }
`;

const QuestionCounter = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #8890a4;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  margin-bottom: 8px;
`;

const ProgressBarTrack = styled.div`
  width: 100%;
  height: 4px;
  background: #eef0f5;
  border-radius: 2px;
  overflow: hidden;
  margin-bottom: 24px;
`;

const ProgressBarFill = styled.div<{ $pct: number; $color: string }>`
  height: 100%;
  width: ${(p) => p.$pct}%;
  background: ${(p) => p.$color};
  border-radius: 2px;
  transition: width 0.4s ease;
`;

/* ── MC Questions ─────────────────────────────────────────────────────────────── */

const QuestionText = styled.p`
  font-size: 17px;
  font-weight: 600;
  color: #1a1a2e;
  line-height: 1.55;
  margin: 0 0 24px;

  @media (max-width: 600px) {
    font-size: 15px;
  }
`;

const OptionsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

function optionBg(variant: string): string {
  if (variant === 'correct') return '#e8f5e9';
  if (variant === 'incorrect') return '#ffebee';
  if (variant === 'missed') return '#e8f5e920';
  return '#ffffff';
}

function optionBorder(variant: string): string {
  if (variant === 'correct') return '#4caf50';
  if (variant === 'incorrect') return '#f44336';
  if (variant === 'missed') return '#4caf5066';
  return '#e4e6ed';
}

const OptionCard = styled.button<{ $variant: string; $disabled: boolean }>`
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  text-align: left;
  padding: 14px 16px;
  border: 2px solid ${(p) => optionBorder(p.$variant)};
  border-radius: 12px;
  background: ${(p) => optionBg(p.$variant)};
  cursor: ${(p) => (p.$disabled ? 'default' : 'pointer')};
  transition: all 0.15s ease;
  position: relative;

  ${(p) =>
    !p.$disabled &&
    css`
      &:hover {
        border-color: #bfc3d1;
        background: #f8f9fc;
        transform: translateY(-1px);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
      }
      &:active {
        transform: translateY(0);
      }
    `}
`;

const OptionLetter = styled.span<{
  $variant: string;
  $color: string;
  $selected: boolean;
}>`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
  flex-shrink: 0;

  ${(p) => {
    if (p.$variant === 'correct')
      return css`
        background: #4caf50;
        color: #fff;
      `;
    if (p.$variant === 'incorrect')
      return css`
        background: #f44336;
        color: #fff;
      `;
    if (p.$variant === 'missed')
      return css`
        background: #4caf5033;
        color: #4caf50;
      `;
    if (p.$selected)
      return css`
        background: ${p.$color};
        color: #fff;
      `;
    return css`
      background: #f0f1f5;
      color: #5f6577;
    `;
  }}
`;

const OptionText = styled.span`
  font-size: 15px;
  font-weight: 500;
  color: #1a1a2e;
  line-height: 1.45;
  flex: 1;
`;

const OptionIcon = styled.span<{ $type: 'correct' | 'incorrect' }>`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
  flex-shrink: 0;

  ${(p) =>
    p.$type === 'correct'
      ? css`
          background: #4caf50;
          color: #fff;
        `
      : css`
          background: #f44336;
          color: #fff;
        `}
`;

/* ── Feedback ─────────────────────────────────────────────────────────────────── */

const FeedbackArea = styled.div`
  margin-top: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  animation: ${fadeIn} 0.3s ease;
`;

const FeedbackBanner = styled.div<{ $type: 'correct' | 'incorrect' }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 700;

  ${(p) =>
    p.$type === 'correct'
      ? css`
          background: #e8f5e9;
          color: #2e7d32;
        `
      : css`
          background: #ffebee;
          color: #c62828;
        `}
`;

const FeedbackIcon = styled.span`
  font-size: 18px;
  line-height: 1;
`;

const ExplanationBox = styled.div`
  background: #f8f9fc;
  border-left: 4px solid #1a73e8;
  border-radius: 0 10px 10px 0;
  padding: 16px 20px;
`;

const ExplanationLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #1a73e8;
  margin-bottom: 6px;
`;

const ExplanationText = styled.p`
  font-size: 14px;
  font-weight: 400;
  color: #333;
  line-height: 1.6;
  margin: 0;
`;

const NextButton = styled.button<{ $color: string }>`
  align-self: flex-end;
  padding: 10px 24px;
  border: none;
  border-radius: 10px;
  background: ${(p) => p.$color};
  color: #ffffff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.15s ease, transform 0.15s ease;

  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

/* ── Results ──────────────────────────────────────────────────────────────────── */

const ResultsCard = styled.div`
  background: #ffffff;
  border-radius: 16px;
  padding: 36px 28px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06), 0 4px 16px rgba(0, 0, 0, 0.04);
  text-align: center;
  animation: ${fadeIn} 0.35s ease;

  @media (max-width: 600px) {
    padding: 28px 16px;
  }
`;

const ResultsHeader = styled.h2`
  font-size: 24px;
  font-weight: 800;
  color: #1a1a2e;
  margin: 0 0 28px;
`;

/* ── Circular Progress ────────────────────────────────────────────────────────── */

const ScoreCircleWrapper = styled.div`
  position: relative;
  width: 140px;
  height: 140px;
  margin: 0 auto 20px;
`;

const ScoreCircleSVG = styled.svg`
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
`;

const ScoreCircleBg = styled.circle`
  fill: none;
  stroke: #eef0f5;
  stroke-width: 10;
`;

const CIRCUMFERENCE = 2 * Math.PI * 52; // ~326.7

const ScoreCircleFill = styled.circle<{ $pct: number; $color: string }>`
  fill: none;
  stroke: ${(p) => p.$color};
  stroke-width: 10;
  stroke-linecap: round;
  stroke-dasharray: ${CIRCUMFERENCE};
  stroke-dashoffset: ${(p) => CIRCUMFERENCE - (CIRCUMFERENCE * p.$pct) / 100};
  animation: ${circleAnim} 0.8s ease forwards;
`;

const ScoreCircleText = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const ScoreNumber = styled.span`
  font-size: 32px;
  font-weight: 800;
  color: #1a1a2e;
  line-height: 1;
`;

const ScoreDetail = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #8890a4;
  margin-top: 4px;
`;

const EncouragingMessage = styled.p`
  font-size: 15px;
  font-weight: 500;
  color: #5f6577;
  line-height: 1.5;
  margin: 0 0 28px;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
`;

const ResultsActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: center;
  max-width: 320px;
  margin: 0 auto;
`;

const ActionButton = styled.button<{
  $variant: 'filled' | 'outline' | 'subtle';
  $color: string;
}>`
  width: 100%;
  padding: 12px 20px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s ease;

  ${(p) => {
    if (p.$variant === 'filled')
      return css`
        background: ${p.$color};
        color: #ffffff;
        border: none;
        &:hover {
          opacity: 0.9;
          transform: translateY(-1px);
        }
      `;
    if (p.$variant === 'outline')
      return css`
        background: transparent;
        color: ${p.$color};
        border: 2px solid ${p.$color};
        &:hover {
          background: ${p.$color}0d;
          transform: translateY(-1px);
        }
      `;
    return css`
      background: transparent;
      color: ${p.$color};
      border: none;
      text-decoration: underline;
      text-underline-offset: 2px;
      &:hover {
        opacity: 0.7;
      }
    `;
  }}

  &:active {
    transform: translateY(0);
  }
`;

/* ── Free Response ────────────────────────────────────────────────────────────── */

const FRQuestionStem = styled.p`
  font-size: 16px;
  font-weight: 500;
  color: #1a1a2e;
  line-height: 1.6;
  margin: 0 0 24px;
  padding-bottom: 20px;
  border-bottom: 1px solid #eef0f5;
`;

const FRPartsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FRPartCard = styled.div<{ $revealed: boolean }>`
  border: 1px solid ${(p) => (p.$revealed ? '#e0e0e0' : '#eef0f5')};
  border-radius: 12px;
  padding: 18px 20px;
  background: ${(p) => (p.$revealed ? '#fafbfd' : '#ffffff')};
  transition: all 0.2s ease;
`;

const FRPartHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 12px;
`;

const FRPartLabel = styled.span`
  font-size: 15px;
  font-weight: 800;
  color: #1a1a2e;
  flex-shrink: 0;
  min-width: 28px;
`;

const FRPartQuestion = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #333;
  line-height: 1.5;
  flex: 1;
`;

const FRPartPoints = styled.span`
  font-size: 11px;
  font-weight: 700;
  color: #8890a4;
  background: #f0f1f5;
  border-radius: 6px;
  padding: 3px 8px;
  white-space: nowrap;
  flex-shrink: 0;
`;

const RevealButton = styled.button<{ $color: string; $enabled: boolean }>`
  padding: 8px 18px;
  border: 1px solid ${(p) => (p.$enabled ? p.$color : '#d0d3dc')};
  border-radius: 8px;
  background: ${(p) => (p.$enabled ? `${p.$color}0d` : '#f5f6f8')};
  color: ${(p) => (p.$enabled ? p.$color : '#aaa')};
  font-size: 13px;
  font-weight: 600;
  cursor: ${(p) => (p.$enabled ? 'pointer' : 'not-allowed')};
  transition: all 0.15s ease;

  ${(p) =>
    p.$enabled &&
    css`
      &:hover {
        background: ${p.$color}1a;
        transform: translateY(-1px);
      }
    `}
`;

const FRSolutionWrapper = styled.div`
  animation: ${slideReveal} 0.4s ease forwards;
  overflow: hidden;
  border-left: 4px solid #4caf50;
  border-radius: 0 8px 8px 0;
  padding: 16px;
  background: #f1f8e9;
  margin-top: 4px;
`;

const FRSolutionLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #4caf50;
  margin-bottom: 6px;
`;

const FRSolutionText = styled.p`
  font-size: 14px;
  font-weight: 400;
  color: #333;
  line-height: 1.6;
  margin: 0;
  white-space: pre-wrap;
`;

/* ── Self Assessment ──────────────────────────────────────────────────────────── */

const SelfAssessArea = styled.div`
  margin-top: 28px;
  padding-top: 24px;
  border-top: 1px solid #eef0f5;
  animation: ${fadeIn} 0.35s ease;
`;

const SelfAssessTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0 0 16px;
  text-align: center;
`;

const SelfAssessOptions = styled.div`
  display: flex;
  gap: 10px;

  @media (max-width: 600px) {
    flex-direction: column;
  }
`;

function assessBg(variant: string): string {
  if (variant === 'good') return '#e8f5e9';
  if (variant === 'ok') return '#fff8e1';
  return '#ffebee';
}

function assessColor(variant: string): string {
  if (variant === 'good') return '#2e7d32';
  if (variant === 'ok') return '#f57f17';
  return '#c62828';
}

function assessBorder(variant: string): string {
  if (variant === 'good') return '#4caf50';
  if (variant === 'ok') return '#ffb300';
  return '#f44336';
}

const SelfAssessButton = styled.button<{ $variant: string }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 16px 12px;
  border: 2px solid ${(p) => assessBorder(p.$variant)};
  border-radius: 12px;
  background: ${(p) => assessBg(p.$variant)};
  color: ${(p) => assessColor(p.$variant)};
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }

  &:active {
    transform: translateY(0);
  }
`;

const SelfAssessEmoji = styled.span`
  font-size: 22px;
  line-height: 1;
`;

const SelfAssessResult = styled.div`
  margin-bottom: 24px;
`;

const SelfAssessResultText = styled.p<{ $variant: string }>`
  font-size: 15px;
  font-weight: 500;
  color: ${(p) => assessColor(p.$variant)};
  line-height: 1.5;
  margin: 0;
  padding: 16px 20px;
  border-radius: 10px;
  background: ${(p) => assessBg(p.$variant)};
`;

/* ── Empty State ──────────────────────────────────────────────────────────────── */

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 24px;
  text-align: center;
`;

const EmptyIcon = styled.span`
  font-size: 48px;
  line-height: 1;
  margin-bottom: 16px;
  display: block;
`;

const EmptyTitle = styled.h2`
  font-size: 22px;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0 0 8px;
`;

const EmptyDescription = styled.p`
  font-size: 14px;
  color: #5f6577;
  margin: 0 0 20px;
  max-width: 400px;
  line-height: 1.5;
`;

const BackLink = styled.button`
  background: none;
  border: none;
  color: #1a73e8;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover {
    opacity: 0.7;
  }
`;
